import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { ArrowRight, KeyRound, Ban, TrendingUp, TrendingDown, Hourglass, Link2, CornerLeftUp, Equal, CircleSlash } from 'lucide-react';
import {
  EFFECT_TYPES,
  EFFECT_TYPE_ICONS,
  EFFECT_TYPE_COLORS,
  PICKABLE_EFFECT_TYPES,
} from '../../config/effectTypes';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * EffectTypePopover — the (+) or a path label expands into the choice, on the map.
 *
 * Deliberately not a modal: no backdrop, no blur, nothing dimmed. The question
 * "how does the next thing follow from this one?" is about the node under the
 * cursor, so it opens next to that node and leaves the map visible.
 *
 * Eight options, all of them visible. A hidden tail behind a "…" saves a few
 * pixels and costs the one thing this step exists for — noticing that "estää"
 * or "heikentää" is the honest answer. At this length the whole vocabulary
 * fits, and the icons let it be scanned by shape rather than read word by word.
 *
 * Anchored with the screen position PathwayNodeLayer already passes to
 * onAddConsequenceClick as its second argument.
 */

const ICONS = { ArrowRight, KeyRound, Ban, TrendingUp, TrendingDown, Hourglass, Link2, CornerLeftUp, Equal, CircleSlash };

const appear = keyframes`
  from { opacity: 0; transform: translate(-8px, -50%) scale(0.96); }
  to   { opacity: 1; transform: translate(0, -50%) scale(1); }
`;

/* Each row arrives just after the card, staggered — the list assembles itself
   rather than blinking into place. */
const riseIn = keyframes`
  from { opacity: 0; transform: translateX(-6px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const Catcher = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: transparent;
`;

const Card = styled.div`
  position: fixed;
  z-index: 1201;
  transform: translateY(-50%);
  animation: ${appear} 0.14s cubic-bezier(0.2, 0.9, 0.3, 1);
  padding: 6px;
  border-radius: 14px;
  /* Fully opaque: the map behind is busy, and a translucent card let node text
     bleed through the choices. */
  background: #14141f;
  border: 1px solid rgba(255, 255, 255, 0.13);
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 205px;
`;

const Choice = styled.button`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 9px 13px;
  border-radius: 10px;
  font-size: 0.94rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  background: ${(props) => (props.$current ? 'rgba(255, 255, 255, 0.06)' : 'transparent')};
  border: 1px solid transparent;
  text-align: left;
  transition: background 0.12s ease, border-color 0.12s ease, transform 0.12s ease;
  white-space: nowrap;
  animation: ${riseIn} 0.16s cubic-bezier(0.2, 0.9, 0.3, 1) both;
  animation-delay: ${(props) => 0.02 + (props.$index || 0) * 0.025}s;

  .icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${(props) => props.$color || 'rgba(255, 255, 255, 0.55)'};
  }

  .label { flex: 1; }

  .polarity {
    font-size: 0.75rem;
    font-weight: 700;
    opacity: 0.35;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: ${(props) => props.$color || 'rgba(255, 255, 255, 0.2)'};
    transform: translateX(2px);
  }

  &:active { transform: scale(0.985); }
`;

const polaritySign = (key) => {
  const p = EFFECT_TYPES[key]?.polarity;
  if (p === '+') return '+';
  if (p === '-') return '−';
  return '';
};

export default function EffectTypePopover({ anchor, value = null, onSelect, onClose }) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [placement, setPlacement] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Keep the card on screen: prefer the right of the (+), flip left when it
  // would overflow, and clamp vertically instead of letting it run off.
  useLayoutEffect(() => {
    if (!anchor || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const margin = 12;
    const gap = 30;
    const half = rect.height / 2;

    // Anchored beside the point it came from — unless it cannot fit there.
    // Off-screen anchors and cards taller than the viewport used to be clamped
    // to an edge, which put the choice somewhere the eye was not; centring is
    // the honest fallback when "next to it" is not available.
    const anchorVisible = anchor.x > -margin && anchor.x < window.innerWidth + margin
      && anchor.y > -margin && anchor.y < window.innerHeight + margin;
    const fitsRight = anchor.x + gap + rect.width <= window.innerWidth - margin;
    const fitsLeft = anchor.x - gap - rect.width >= margin;
    const fitsVertically = rect.height + margin * 2 <= window.innerHeight;

    if (!anchorVisible || (!fitsRight && !fitsLeft) || !fitsVertically) {
      setPlacement({
        left: Math.max(margin, (window.innerWidth - rect.width) / 2),
        top: window.innerHeight / 2,
      });
      return;
    }

    const left = fitsRight ? anchor.x + gap : anchor.x - gap - rect.width;
    const top = Math.min(Math.max(anchor.y, half + margin), window.innerHeight - half - margin);
    setPlacement({ left, top });
  }, [anchor]);

  if (!anchor) return null;

  return (
    <>
      <Catcher onClick={onClose} />
      <Card
        ref={cardRef}
        style={{ left: placement.left, top: placement.top }}
        onClick={(e) => e.stopPropagation()}
      >
        {PICKABLE_EFFECT_TYPES.map((key, i) => {
          const Icon = ICONS[EFFECT_TYPE_ICONS[key]] || ArrowRight;
          return (
            <Choice
              key={key}
              $index={i}
              $color={EFFECT_TYPE_COLORS[key]}
              $current={value === key}
              type="button"
              onClick={() => onSelect?.(key)}
              title={key}
            >
              <Icon className="icon" />
              <span className="label">{t(`impactMapV2.effectTypes.${key}`)}</span>
              <span className="polarity">{polaritySign(key)}</span>
            </Choice>
          );
        })}
      </Card>
    </>
  );
}
